import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetPasswordMutation } from "../../../../../redux/api/auth/authAPI";
import { useResetPasswordMutation } from "../../../../../redux/api/user/userAPI";
import {
  selectAuth,
  setPrivileges,
  setToken,
} from "../../../../../redux/features/auth/auth-slice";
import { clearProfile } from "../../../../../redux/features/user/user-slice";
import { AppRoute } from "../../../interfaces/app-routes";
import { IHttpError } from "../../../interfaces/http-errror";
import AppButton from "../../../ui-elements/buttons/app-button/app-button";
import AppInput from "../../../ui-elements/input/app-input";
import { passwordRegex } from "../../../utils/regex";
import { showErrorMessage, showSuccessMessage } from "../../../utils/toast";

interface ILocation {
  isNavigateToProfile: boolean;
}

interface ResetPassword {
  password: string;
  confirmPassword: string;
}

const defaultFormValues: ResetPassword = {
  password: "",
  confirmPassword: "",
};

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const location = useLocation();
  const isNavigateToProfile = (location.state as ILocation)
    ?.isNavigateToProfile;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [triggerResetPassword, { isLoading: isLoadingResetPassword }] =
    useResetPasswordMutation();
  const [triggerSetPassword, { isLoading: isLoadingSetPassword }] =
    useSetPasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResetPassword>({
    defaultValues: { ...defaultFormValues },
  });

  const handleResetStorage = () => {
    localStorage.clear();
    dispatch(setToken(""));
    dispatch(setPrivileges([]));
    dispatch(clearProfile());
    navigate(AppRoute.LOGIN);
  };

  const onSubmit = async (data: ResetPassword) => {
    const payload = { password: data.password };
    const triggerMutation =
      auth.token && isNavigateToProfile
        ? triggerSetPassword
        : triggerResetPassword;

    await triggerMutation(payload)
      .unwrap()
      .then(() => {
        showSuccessMessage(
          auth.token
            ? "Password created successfully"
            : "Password reset successfully",
        );
        handleResetStorage();
        reset({ ...defaultFormValues });
      })
      .catch((error: IHttpError) => {
        showErrorMessage(
          error.message ||
            `${auth.token ? `Your password setup link has expired. Please click the verification link again.` : `This password reset link has expired or is no longer valid. Please request a new link or use the latest link.`}`,
        );
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col className="mt-4">
          <AppInput
            name="password"
            leftIcon="key"
            icon={!showPassword ? "visibility_off" : "visibility"}
            placeholder="New Password"
            onIconClick={() => setShowPassword(!showPassword)}
            type={showPassword ? "text" : "password"}
            register={register("password", {
              required: "Password is required",
              pattern: {
                value: passwordRegex,
                message:
                  "Password must have 6-20 characters and include at least one lowercase letter, one uppercase letter, one numeric and one special character",
              },
            })}
            errors={errors}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <AppInput
            name="confirmPassword"
            leftIcon="key"
            icon={!showConfirmPassword ? "visibility_off" : "visibility"}
            placeholder="Confirm New Password"
            onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
            type={showConfirmPassword ? "text" : "password"}
            register={register("confirmPassword", {
              required: "Password confirmation is required",
              validate: (value, formValues) => {
                if (value !== formValues.password) {
                  return "Password doesn't match";
                }
              },
            })}
            errors={errors}
          />
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <AppButton
            text="Confirm"
            size="medium"
            type="submit"
            isLoading={isLoadingResetPassword || isLoadingSetPassword}
          />
        </Col>
      </Row>
    </form>
  );
};

export default ResetPasswordForm;
